import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { createEmployeeApi, getEmployeeByUserIdApi, updateEmployeeApi } from '../../../../Api/Employee';

type User = {
  id: string;
  name: string;
  email: string;
  mobile: string;
  gender: number | null;
  address: string;
  roleType: string;
};

export interface EmployeeInterface {
  id: string;
  userId: string;
  employeeType: string;
  permanentAddress: string;
  isActive: boolean;
  salary: number;
  hourlyRate: number;
  joiningDate: string;
  leavingDate?: string | null;
  updatedBy: string;
  updatedOn: string;
}

interface Props {
  url: string;
  Employee: EmployeeInterface | null;
  setEmployee: React.Dispatch<React.SetStateAction<EmployeeInterface | null>>;
  selectedUser: User | null;
}

const EmployeeInfo = ({ url, Employee, setEmployee, selectedUser }: Props) => {
  const [loader, setLoader] = useState(false);

  const fetchEmployeeData = async () => {
    if (!selectedUser?.id) return;

    try {
      const employeeData = await getEmployeeByUserIdApi(url, selectedUser.id);
      if (employeeData) {
        setEmployee(employeeData);
      } else {
        // If no data exists, initialize empty object for new entry
        setEmployee({
          id: "",
          userId: selectedUser.id,
          employeeType: "",
          permanentAddress: selectedUser.address ?? "",
          isActive: false,
          salary: 0,
          hourlyRate: undefined ,
          joiningDate: "",
          leavingDate: null,
          updatedBy: "",
          updatedOn: "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch employee data:", error);
      toast.error("Failed to fetch employee data. Please try again.");
    }
  };

  useEffect(() => {

    fetchEmployeeData();
  }, [selectedUser, url]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    setEmployee((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]:
          type === 'number'
            ? value === '' ? null : Number(value)
            : value,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!Employee || !selectedUser) {
      console.error("Employee or user data is missing.");
      return;
    }
    if (!Employee.employeeType || !Employee.salary || !Employee.joiningDate || !Employee.permanentAddress) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      setLoader(true);

      if (!Employee.id) {
        // Create new employee
        const response = await createEmployeeApi(url, {
          EmployeeType: Employee.employeeType,
          PermanentAddress: Employee.permanentAddress,
          Salary: Employee.salary,
          HourlyRate: Employee.hourlyRate ?? undefined,
          JoiningDate: Employee.joiningDate,
          LeavingDate: Employee.leavingDate || null,
          IsActive: Employee.isActive,
          UserId: selectedUser.id,
        });
        toast.success("Employee created successfully!");
      } else if (Employee.id) {
        await updateEmployeeApi(url, {
          Id: Employee.id,
          EmployeeType: Employee.employeeType,
          PermanentAddress: Employee.permanentAddress,
          Salary: Employee.salary,
          HourlyRate: Employee.hourlyRate ?? undefined,
          JoiningDate: Employee.joiningDate,
          LeavingDate: Employee.leavingDate || null,
          IsActive: Employee.isActive,
        });
        await fetchEmployeeData();
        toast.success("Employee updated successfully!");
      }
    } catch (error) {
      console.error("Failed to save employee:", error);
      toast.error("Failed to save employee. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 p-4 border border-gray-300 rounded-md bg-gray-50">
      <h2 className="text-2xl font-semibold mb-3">Employment Info</h2>

      <div className="my-4 gap-4">
        {/* Employee type */}
        <div className="flex flex-col space-y-2 w-full">
          <label className="text-gray-900 dark:text-gray-200 font-semibold">Employee type</label>
          <select
            required
            name="employeeType"
            value={Employee?.employeeType ?? ""}
            onChange={handleChange}
            className="w-full px-4 py-2 mt-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring focus:ring-blue-300 focus:outline-none"
          >
            <option value="">Select Employee type</option>
            <option value="Full time">Full time</option>
            <option value="Part time">Part time</option>
            <option value="Hourly">Hourly</option>
          </select>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {/* Salary */}
          <div className="flex flex-col space-y-2 w-full">
            <label className="text-gray-900 dark:text-gray-200 font-semibold">Salary</label>
            <input
              required
              name="salary"
              type="number"
              value={Employee?.salary !== undefined && Employee?.salary !== null ? String(Employee.salary) : ""}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Salary"
            />
          </div>

          {/* Hourly rate */}
          <div className="flex flex-col space-y-2 w-full">
            <label className="text-gray-900 dark:text-gray-200 font-semibold">Hourly rate</label>
            <input
              name="hourlyRate"
              type="number"
              value={Employee?.hourlyRate !== undefined && Employee?.hourlyRate !== null ? String(Employee.hourlyRate) : ""}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Hourly rate"
            />
          </div>

          {/* Joining Date */}
          <div className="flex flex-col space-y-2 w-full">
            <label className="text-gray-900 dark:text-gray-200 font-semibold">Joining Date</label>
            <input
              required
              name="joiningDate"
              type="date"
              value={Employee?.joiningDate?.slice(0, 10) ?? ""}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Joining Date"
            />
          </div>

          {/* Leaving Date */}
          <div className="flex flex-col space-y-2 w-full">
            <label className="text-gray-900 dark:text-gray-200 font-semibold">Leaving Date</label>
            <input
              name="leavingDate"
              type="date"
              value={Employee?.leavingDate ?? ""}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Leaving Date"
            />
          </div>

          <div className="flex items-center space-x-2 mt-4">
            <input
              type="checkbox"
              name="isActive"
              checked={Employee?.isActive || false}
              onChange={(e) =>
                setEmployee((prev) =>
                  prev ? { ...prev, isActive: e.target.checked } : prev
                )
              }
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label className="text-sm font-medium text-gray-900 dark:text-gray-300">
              Is Active
            </label>
          </div>


        </div>
      </div>

      <button
        type="submit"
        disabled={loader}
        className={`mt-4 px-4 py-2 text-white rounded-md ${loader ? "bg-gray-400" : "bg-blue-500 hover:bg-blue-600"
          }`}
      >
        {Employee?.id ? "Update" : "Submit"}
      </button>

    </form>
  );
};

export default EmployeeInfo;
